import React, { useImperativeHandle, useState } from 'react';
import { useSprings, animated } from 'react-spring';
import { GestureState, createPanResponder, gestureInitialState } from 'helpers/pan-responder';
import { useWindowSize } from 'hooks/useWindowSize';
import { CarouselWrapper, Wrapper } from './styled';
import { useScreenBreakpoint } from 'consts/breakpoints';

interface InfinityCarouselProps {
  disableSwipe?: boolean;
  elementWidth: number;
  elementHeight: number;
  children: React.ReactNode;
  spacing?: number;
  startIndex?: number;
  position?: 'center' | undefined;
  onChangeEvent?: (direction: 'left' | 'right', isDesktop: boolean) => void;
}

export interface ForwardRefProps {
  move: (direction: InfinityDirection, userAction?: boolean) => void;
}

export enum InfinityDirection {
  Next = 1,
  Prev = -1,
}

export const InfiniteSlider = React.forwardRef<ForwardRefProps, InfinityCarouselProps>((props, forwardedRef) => {
  const { children, elementHeight, elementWidth, disableSwipe, spacing = 0, startIndex = 0, onChangeEvent } = props;
  const [ gestureState, setGestureState ] = useState<GestureState>(gestureInitialState);
  const [ offset, setOffset ] = useState(startIndex);
  const isDesktop = useScreenBreakpoint() >= 3;
  const [ windowWidth ] = useWindowSize();
  const isCentered = props.position === 'center';

  const childrenArray = React.Children.toArray(children);
  const arr = [ ...childrenArray, ...childrenArray, ...childrenArray ];
  const itemsCount = arr.length;
  const finalElementWidth = elementWidth + spacing;

  const springs = useSprings(
    itemsCount,
    arr.map((_, i) => {
      const { dx } = gestureState;
      let currentPosition = ((i - offset + itemsCount) % itemsCount) * -finalElementWidth + childrenArray.length * finalElementWidth + (isCentered ? (finalElementWidth / 2 - windowWidth / 2) : 0);
      if (dx) {
        currentPosition += dx;
      }
      const immediate = !dx && currentPosition <= (childrenArray.length + 1) * -finalElementWidth || currentPosition >= childrenArray.length * finalElementWidth + (isCentered ? (finalElementWidth / 2 - windowWidth / 2) : 0);
      return {
        x: currentPosition,
        immediate,
        config: { tension: 180, friction: 20 },
      };
    })
  );

  const panHandlers = createPanResponder({
    onStart: (state) => setGestureState(state),
    onMove: (state) => {
      setGestureState(state);
    },
    onEnd: (s) => {
      setGestureState((prevGState: any) => {
        if (prevGState) {
          if (prevGState.dx > 0) scroll(InfinityDirection.Next);
          else if (prevGState.dx < 0)  scroll(InfinityDirection.Prev);
        }
        return {
          ...s,
          prevGState,
        };
      });
    },
  });

  useImperativeHandle(forwardedRef, () => ({
    move(d, userAction) {
      scroll(d, userAction);
    },
  }));

  const scroll = (newOffset: number, userAction: boolean = true) => {
    setOffset((prevOffset) => (prevOffset + newOffset + itemsCount) % itemsCount);
    if (onChangeEvent && userAction) {
      onChangeEvent(newOffset > 0 ? 'left' : 'right', isDesktop);
    }
  };

  return (
    <Wrapper {...(disableSwipe ? {} : panHandlers)}>
      <CarouselWrapper elHeight={elementHeight}>
        {springs.map(({ x }, i) => (
          <animated.div
            key={i}
            data-key={i}
            style={{
              position: 'absolute',
              transform: x.to((xPos) => `translateX(${xPos}px)`),
            }}
          >
            {arr[i]}
          </animated.div>
        ))}
      </CarouselWrapper>
    </Wrapper>
  );
});
